/**
 * Live Platform Telemetry
 * ---------------------------------------------------------------
 * Operational console showing active platform streams, top live
 * hooks, replay spikes, save velocity, behavioral drift warnings,
 * and cross-platform trend shifts.
 *
 * NOT social analytics. A live behavioral monitoring system.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Activity, Radio, Loader2, Waves, AlertTriangle, ArrowUpRight, ArrowDownRight, Zap,
} from "lucide-react";
import {
  generateMockIngestion, ingestTelemetry, LIVE_TELEMETRY_EVENT, type IngestPlatform,
} from "@/lib/platformIngestion";

const PLATFORM_LABEL: Record<string, string> = {
  tiktok: "TikTok",
  youtube: "YouTube Shorts",
  instagram: "Instagram Reels",
};

const PLATFORM_TONE: Record<string, string> = {
  tiktok: "border-cyan/40 text-cyan",
  youtube: "border-red-400/40 text-red-300",
  instagram: "border-amber-400/40 text-amber-300",
};

interface IngestionRow {
  id: string;
  platform: string;
  hook: string | null;
  archetype: string | null;
  format: string | null;
  escalation_path: string | null;
  replay_rate: number;
  completion_rate: number;
  save_rate: number;
  share_rate: number;
  comment_velocity: number;
  engagement_score: number;
  virality_score: number;
  prediction_accuracy: number | null;
  collected_at: string;
}

interface DriftRow {
  id: string;
  platform: string;
  behavioral_pattern: string;
  trend_direction: string;
  confidence: number;
  signal_strength: number;
  delta: number;
  detected_at: string;
}

const TICKERS = [
  "Telemetry stream open · TikTok",
  "Replay spike detected · Reels",
  "Save velocity rising · Shorts",
  "Cross-platform drift sweep",
  "Synthetic vs reality reconciling",
  "Hook fatigue scan · live",
];

const LivePlatformTelemetry = () => {
  const [rows, setRows] = useState<IngestionRow[]>([]);
  const [drift, setDrift] = useState<DriftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [volume, setVolume] = useState<number[]>([50]);
  const [platforms, setPlatforms] = useState<IngestPlatform[]>(["tiktok", "youtube", "instagram"]);
  const [tickerIdx, setTickerIdx] = useState(0);
  const tickRef = useRef(0);
  const [, force] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: r }, { data: d }] = await Promise.all([
      (supabase as any)
        .from("platform_signal_ingestion")
        .select("id, platform, hook, archetype, format, escalation_path, replay_rate, completion_rate, save_rate, share_rate, comment_velocity, engagement_score, virality_score, prediction_accuracy, collected_at")
        .order("collected_at", { ascending: false })
        .limit(400),
      (supabase as any)
        .from("behavioral_platform_drift")
        .select("id, platform, behavioral_pattern, trend_direction, confidence, signal_strength, delta, detected_at")
        .order("detected_at", { ascending: false })
        .limit(60),
    ]);
    setRows((r ?? []) as IngestionRow[]);
    setDrift((d ?? []) as DriftRow[]);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onIngest = () => void load();
    window.addEventListener(LIVE_TELEMETRY_EVENT, onIngest);
    return () => window.removeEventListener(LIVE_TELEMETRY_EVENT, onIngest);
  }, [load]);

  useEffect(() => {
    const id = window.setInterval(() => {
      tickRef.current += 1;
      setTickerIdx((i) => (i + 1) % TICKERS.length);
      force((v) => v + 1);
    }, 3500);
    return () => window.clearInterval(id);
  }, []);

  const handleRun = async () => {
    if (platforms.length === 0) return;
    setRunning(true);
    try {
      const generated = await generateMockIngestion({ count: volume[0], platforms });
      await ingestTelemetry(generated);
    } finally {
      setRunning(false);
    }
  };

  // ─── Derived: per-platform stream summary ───────────────────
  const streams = useMemo(() => {
    const map = new Map<string, { count: number; replay: number[]; save: number[]; virality: number[]; latest: string }>();
    rows.forEach((r) => {
      if (!map.has(r.platform)) map.set(r.platform, { count: 0, replay: [], save: [], virality: [], latest: r.collected_at });
      const x = map.get(r.platform)!;
      x.count += 1;
      x.replay.push(r.replay_rate);
      x.save.push(r.save_rate);
      x.virality.push(r.virality_score);
    });
    const avg = (a: number[]) => (a.length ? a.reduce((s, n) => s + n, 0) / a.length : 0);
    return Array.from(map.entries()).map(([platform, x]) => ({
      platform,
      count: x.count,
      avg_replay: avg(x.replay),
      avg_save: avg(x.save),
      avg_virality: avg(x.virality),
      latest: x.latest,
    })).sort((a, b) => b.avg_virality - a.avg_virality);
  }, [rows]);

  const topHooks = useMemo(() => {
    return [...rows]
      .filter((r) => r.hook)
      .sort((a, b) => b.virality_score - a.virality_score)
      .slice(0, 8);
  }, [rows]);

  const replaySpikes = useMemo(() => {
    return [...rows]
      .sort((a, b) => b.replay_rate - a.replay_rate)
      .slice(0, 6);
  }, [rows]);

  const saveVelocity = useMemo(() => {
    return [...rows]
      .sort((a, b) => b.save_rate - a.save_rate)
      .slice(0, 6);
  }, [rows]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Radio className="w-4 h-4 text-cyan animate-pulse" />
          Live Platform Telemetry
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
            Behavioral signal ingestion · TikTok · Shorts · Reels
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Live ticker + controls */}
        <div className="rounded-lg border border-cyan/20 bg-charcoal-deep/60 p-3 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-cyan opacity-70 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan" />
            </span>
            <span className="text-[11px] uppercase tracking-wider font-mono text-muted-foreground truncate">
              {TICKERS[tickerIdx]}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">Volume</span>
              <div className="w-32"><Slider value={volume} onValueChange={setVolume} min={10} max={500} step={10} /></div>
              <span className="text-[10px] font-mono text-cyan w-8 text-right">{volume[0]}</span>
            </div>
            <ToggleGroup
              type="multiple"
              value={platforms}
              onValueChange={(v) => setPlatforms(v as IngestPlatform[])}
              className="gap-1"
            >
              {(["tiktok", "youtube", "instagram"] as IngestPlatform[]).map((p) => (
                <ToggleGroupItem
                  key={p}
                  value={p}
                  className="text-[10px] uppercase tracking-wider font-mono h-7 px-2 border border-glass-border data-[state=on]:bg-cyan/15 data-[state=on]:text-cyan"
                >
                  {p}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <Button
              variant="outline" size="sm"
              onClick={handleRun}
              disabled={running || platforms.length === 0}
              className="border-cyan/40 text-cyan hover:bg-cyan/10 font-mono text-[10px] uppercase tracking-wider"
            >
              {running ? (<><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Ingesting</>) : (<><Zap className="w-3 h-3 mr-1.5" />Ingest Telemetry</>)}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-6 h-6 animate-spin text-cyan" />
          </div>
        ) : (
          <>
            {/* Active streams */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-mono mb-2">
                Active Platform Streams
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {(["tiktok", "youtube", "instagram"] as const).map((p) => {
                  const s = streams.find((x) => x.platform === p);
                  return (
                    <div key={p} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className={`text-[10px] font-mono uppercase ${PLATFORM_TONE[p]}`}>
                          {PLATFORM_LABEL[p]}
                        </Badge>
                        <span className="text-[10px] font-mono text-muted-foreground">
                          {s ? `${s.count} signals` : "no stream"}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mt-3">
                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Replay</div>
                          <div className="text-sm font-mono text-amber-300">{s ? Math.round(s.avg_replay) : 0}%</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Save</div>
                          <div className="text-sm font-mono text-emerald-300">{s ? s.avg_save.toFixed(1) : "0.0"}%</div>
                        </div>
                        <div>
                          <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Virality</div>
                          <div className="text-sm font-mono text-cyan">{s ? Math.round(s.avg_virality) : 0}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Top hooks + spikes + save velocity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-2 flex items-center gap-1">
                  <Activity className="w-3 h-3 text-cyan" />Top Live Hooks
                </div>
                <ScrollArea className="max-h-[240px]">
                  <div className="divide-y divide-glass-border">
                    {topHooks.length === 0 ? (
                      <div className="text-[11px] text-muted-foreground py-3">Awaiting telemetry…</div>
                    ) : topHooks.map((h, i) => {
                      const pulse = (tickRef.current + i) % 5 === 0;
                      return (
                        <div key={h.id} className="py-2">
                          <div className="text-xs italic text-foreground line-clamp-2">"{h.hook}"</div>
                          <div className="flex items-center gap-2 mt-1 text-[10px] font-mono uppercase tracking-wider">
                            <span className={PLATFORM_TONE[h.platform] ?? "text-muted-foreground"}>{h.platform}</span>
                            <span className={`${pulse ? "text-cyan" : "text-amber-300"} transition-colors`}>VIR {Math.round(h.virality_score)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>

              <div className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-2 flex items-center gap-1">
                  <Waves className="w-3 h-3 text-amber-300" />Replay Spikes
                </div>
                <div className="space-y-2">
                  {replaySpikes.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground py-3">No spikes detected.</div>
                  ) : replaySpikes.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-muted-foreground truncate max-w-[60%]">{r.archetype ?? "—"}</span>
                      <span className="text-amber-300">+{Math.round(r.replay_rate)}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono mb-2 flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3 text-emerald-300" />Save Velocity
                </div>
                <div className="space-y-2">
                  {saveVelocity.length === 0 ? (
                    <div className="text-[11px] text-muted-foreground py-3">No velocity data.</div>
                  ) : saveVelocity.map((r) => (
                    <div key={r.id} className="flex items-center justify-between text-[11px] font-mono">
                      <span className="text-muted-foreground truncate max-w-[60%]">{r.format ?? "—"}</span>
                      <span className="text-emerald-300">{r.save_rate.toFixed(1)}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Drift warnings */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-mono mb-2 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-300" />
                Platform Drift Warnings · Behavioral Trend Shifts
              </div>
              {drift.length === 0 ? (
                <div className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-4 text-[11px] text-muted-foreground font-mono text-center">
                  No drift detected · monitoring streams
                </div>
              ) : (
                <ScrollArea className="max-h-[220px]">
                  <div className="space-y-1.5">
                    {drift.map((d) => {
                      const up = d.trend_direction === "rising";
                      return (
                        <div key={d.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-glass-border bg-charcoal-deep/30">
                          <Badge variant="outline" className={`text-[9px] font-mono uppercase ${PLATFORM_TONE[d.platform] ?? "border-glass-border"}`}>
                            {d.platform}
                          </Badge>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs text-foreground truncate">{d.behavioral_pattern}</div>
                            <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                              Confidence {Math.round(d.confidence)}% · Signal {Math.round(d.signal_strength)}
                            </div>
                          </div>
                          <div className={`flex items-center gap-1 text-xs font-mono ${up ? "text-emerald-300" : "text-red-300"}`}>
                            {up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                            {d.delta > 0 ? "+" : ""}{d.delta.toFixed(1)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LivePlatformTelemetry;
